"use client";

// OrganismIdentity — the one place a signed-in surface declares "this user's
// organism is THIS palette". It pushes the identity-minted ColorState into the
// ThemeProvider, which is the single authority for the --organism-* variables
// the whole UI reads (folder tint, avatar, glows, ink hue). Rendering it on
// every signed-in surface (the dashboard shell + the shared PageHeader) is what
// makes the colour consistent EVERYWHERE — no more default-green folders on one
// page and a tinted organism on another. Server-safe to drop into a server
// component as a child; it renders nothing.

import { useEffect, useLayoutEffect } from "react";
import { useTheme } from "./ThemeProvider";
import { organismFromSeed } from "../lib/organism/identity";
import type { ColorState } from "../lib/organism/color";

const useIso = typeof window !== "undefined" ? useLayoutEffect : useEffect;

export default function OrganismIdentity({
  identity,
  palette,
}: {
  identity: string | null | undefined;
  /** an explicit palette that WINS over the identity mint — the netti a person
   *  was born with on the birth page, carried into their account (law_one_netti:
   *  it is still one creature per person; the seed simply came from the birth,
   *  not the email). */
  palette?: ColorState | null;
}) {
  const { setOrganism } = useTheme();
  useIso(() => {
    if (palette) { setOrganism(palette); return; }
    // only seed for a real signed-in identity — never override the public/default
    // organism on a surface that happens to render without a user.
    if (!identity) return;
    setOrganism(organismFromSeed(identity));
    // seed once per identity — the ground may change independently, but the
    // organism palette is a pure function of who the user is.
  }, [identity, palette]);

  // THE DESKTOP SHELL IS LISTENING. The Mac app's living wallpaper should wear
  // the same creature as the window in front of it, so a signed-in surface
  // announces who it is; the app's preload forwards it. A no-op in a browser.
  useEffect(() => {
    if (!identity) return;
    if (!(window as unknown as { innernetDesktop?: unknown }).innernetDesktop) return;
    window.dispatchEvent(new CustomEvent("innernet:identity", { detail: { identity } }));
  }, [identity]);

  return null;
}
